import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { BoardPreviewMap, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { equal } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormMultiInputComponent from 'webapp/components/form/multi-input';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';

interface Args {
	model: Board | BoardLoop;
	isContentShown: boolean;
	openLinkTab: () => void;
	onClose: () => void;
	previewHash?: BoardPreviewMap;
}

export default class ShareStreamToTvComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;

	@tracked submitInProgress = false;

	@tracked code = ['', '', '', ''];
	@tracked codeStatus = '';

	// @tracked accesibleToOtherUsers = false;
	// @tracked tvName = 'Office TV';

	get codeString() {
		return this.code.join('');
	}

	get shownUrlForTV() {
		let url;
		if (this.accountService.isDomainCustom) {
			url = `${this.accountService.domain}/tv`;
		} else {
			url = 'tv.databox.com';
		}
		return url;
	}

	get datawallUrl() {
		return this.args.model.getDatawallUrlWithPreviewHash(this.args.previewHash);
	}

	get isTabDisabled() {
		return !this.args.model.isPublicEnabled;
	}

	@action
	streamToTV() {
		const code = this.code.join(''),
			url = this.datawallUrl;

		if (code.length !== 4) {
			this.codeStatus = 'error';
			return;
		}

		this.submitInProgress = true;
		this.requestService
			.api('POST', '/boards/stream', { code, url })
			.then(
				() => {
					this.codeStatus = 'ok';
					this.analyticsService.event(TrackingEvent.STREAMING_STARTED);
					this.infolayerService.ok('Streaming started successfully!');
					this.args.onClose();
				},
				(errorData) => {
					this.codeStatus = 'error';
					throw errorData;
				},
			)
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	changeCode(newCode: string) {
		const code = Array<string>(4).fill('');
		newCode.split('').forEach((v, k) => {
			code[k] = v;
		});
		if (!equal(code, this.code)) {
			this.code = code;
		}
		this.codeStatus = ''; // resetting error or valid status
	}

	@action
	focusFirstCodeInput(node: HTMLElement) {
		node.querySelector('input')?.focus();
	}

	// @action
	// makeAccessibleToOtherUsers() {
	// 	// TODO: Implement make accessible to other users
	// }
	<template>
		<div
			class="content2
				{{if @isContentShown 'content2--shown'}}
				stream-to-tv
				{{if this.isTabDisabled 'disabled'}}"
		>

			{{#if this.isTabDisabled}}
				<p class="disabled-warning">
					<span>
						To stream to TV, turn on the
						<strong>public link</strong>
						on the ‘Shareable link’ tab.
					</span>
					<a class="button size-tiny" {{on "click" @openLinkTab}}>Open shareable link</a>
				</p>
			{{/if}}

			<div class="row">
				<div class="left">
					<p>To stream this dashboard to a Smart TV:</p>
					<ul class="instructions">
						<li>
							<CommonUiIconComponent @name="NumberCircle1Fill" @size="lg" />
							<span>Navigate to <b>{{this.shownUrlForTV}}</b> on your Smart TV,</span>
						</li>
						<li>
							<CommonUiIconComponent @name="NumberCircle2Fill" @size="lg" />
							<span>Input the code your see on the TV screen below.</span>
						</li>
					</ul>
				</div>
				<div class="right">
					<div class="tv">
						<img src="/images/placeholders/tv-databox.png" alt />
						<span class="text">{{this.shownUrlForTV}}</span>
					</div>
				</div>
			</div>

			<div class="row row--code">
				<div class="left code {{this.codeStatus}}" {{didInsert this.focusFirstCodeInput}}>
					<strong>Code</strong>
					<FormMultiInputComponent
						@length={{4}}
						@value={{this.codeString}}
						@onChange={{this.changeCode}}
					/>
					<span class="icon"></span>
				</div>
				{{!--
        <div class="right tv-name">
            <strong>TV name <span class="italic">(optional)</span></strong>
            <Form::Input
                name="tv_name"
                maxlength="65"
                @value={{this.tvName}}
                disabled={{false}}
            />
        </div>
        --}}
			</div>
			{{!--
    <div class="row">
        <Form::Checkbox
            @name="accesible_to_other_users"
            @selected={{this.accesibleToOtherUsers}}
            @label="Make TV screen accessible to other users"
            @isDisabled={{false}}
            @onChange={{this.makeAccessibleToOtherUsers}}
        />
    </div>
    --}}
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{@onClose}}
					/>
					<SubmitButtonComponent
						@text="Stream to TV"
						@extraClass="save"
						@onTrigger={{this.streamToTV}}
						@noDelay={{true}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>
		</div>
	</template>
}
